import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PackageSummary } from '../api';
import { useAuth } from '../auth';
import { Logo } from '../components/Layout';
import { Badge, Button, Input, Spinner } from '../components/ui';

export default function Landing() {
  const { user } = useAuth();
  const [pkgs, setPkgs] = useState<PackageSummary[] | null>(null);
  const [q, setQ] = useState('');

  async function load(query = '') {
    const url = `/api/v1/packages${query ? `?q=${encodeURIComponent(query)}` : ''}`;
    const res = await fetch(url, { credentials: 'include' });
    const body = (await res.json()) as { packages: PackageSummary[] };
    setPkgs(body.packages);
  }
  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 font-bold text-slate-900">
            <Logo /> SwarmAI Hub
          </Link>
          <nav className="flex items-center gap-3 text-sm">
            <a href="/docs/REMOTE-PUBLISHING.md" className="hidden text-slate-600 hover:text-slate-900 sm:inline">
              Publish
            </a>
            {user ? (
              <Link to="/dashboard">
                <Button>Dashboard</Button>
              </Link>
            ) : (
              <Link to="/auth/login">
                <Button>Sign in</Button>
              </Link>
            )}
          </nav>
        </div>
      </header>

      <section className="bg-gradient-to-b from-brand-50 to-slate-50">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            The registry for SwarmAI plugins
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-slate-600">
            Discover tools, channels, skills, themes and more — or publish your own with a Personal Access Token.
          </p>
          <form
            className="mx-auto mt-8 flex max-w-md gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void load(q);
            }}
          >
            <Input placeholder="Search packages…" value={q} onChange={(e) => setQ(e.target.value)} />
            <Button type="submit">Search</Button>
          </form>
        </div>
      </section>

      <main className="mx-auto max-w-7xl px-6 py-10">
        {!pkgs ? (
          <div className="flex justify-center py-20">
            <Spinner />
          </div>
        ) : pkgs.length === 0 ? (
          <p className="py-20 text-center text-slate-500">No packages found.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pkgs.map((p) => (
              <div key={p.id} className="rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200 transition hover:shadow-md">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="truncate font-semibold text-slate-900">{p.name}</h3>
                  <Badge color={p.trust === 'official' ? 'blue' : p.trust === 'verified' ? 'green' : 'slate'}>
                    {p.trust}
                  </Badge>
                </div>
                <code className="text-xs text-slate-400">{p.id}</code>
                <p className="mt-2 line-clamp-2 h-10 text-sm text-slate-600">{p.description}</p>
                <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                  <span>
                    <Badge>{p.kind}</Badge> <span className="ml-1">v{p.latestVersion}</span>
                  </span>
                  <span>⬇ {p.downloadCount}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
